import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

// Material UI
import { makeStyles } from '@material-ui/core/styles'
import Avatar from '@material-ui/core/Avatar'
import EqualizerIcon from '@material-ui/icons/Equalizer'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'

// Styles
import Tooltip from 'components/Tooltip'
import { Container, MessageContainer } from './styles'

// Components
import TableCell from './components/TableCell'
import { IStore } from 'store/modules/store/types'
import { BootstrapTooltip } from 'components/BootstrapTooltip'

const useStyles = makeStyles(theme => ({
  large: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    marginTop: theme.spacing(4)
  },
  blue: {
    marginTop: theme.spacing(4),
    color: '#fff',
    backgroundColor: '#495BD4'
  },
  labelCell: {
    width: '250px'
  },
  customIcon: {
    fontSize: '128px',
    color: '#CCCCCC',
    backgroundColor: '#FAFAFA',
    borderRadius: '155px',
    padding: theme.spacing(4),
    marginBottom: '18px'
  }
}))

interface ISeller {
  activities: {
    _id: string
    date: string
    sellerId: string
    type: string
  }[]
  events: {
    _id: string
    entryType: string
    period: {
      start: string
      end: string
    }
    selected: {
      options: []
      title: string
    }
    sellerId: string
    success: boolean
  }[]
  fail: number
  success: number
  sellerId: {
    _id: string
    name: {
      first: string
      last: string
      complete: string
    }
    photo: {
      url: string | undefined
    }
  }
  time: {
    line: number
    working: number
  }
}

interface IContent {
  name: string
  total: number
  success: boolean
  sellers: {
    sellerId: string
    total: number
  }[]
}

interface HeatMapProps {
  sellers: ISeller[]
  totalSales: number
  total: number
  store: IStore
  accuracies?: { [sellerId: string]: number }
  totalAccuracy?: number
  realSales?: { [sellerId: string]: number }
  totalRealSales?: number
}

const HeatMap = ({
  sellers,
  totalSales,
  total,
  store,
  accuracies,
  totalAccuracy,
  realSales,
  totalRealSales
}: HeatMapProps) => {
  const classes = useStyles()

  const [header, setHeader] = useState<any[]>([[], [], [], [], [], []])
  const [content, setContent] = useState<IContent[]>([])

  const salesCases = ['Venda padrão', 'Venda por WhatsApp', 'Troca com venda']

  useEffect(() => {
    if (sellers) {
      setHeader(
        sellers.reduce(
          (acc, curr, index) => {
            acc[0].push(
              <th
                style={{ marginBottom: '28px' }}
                key={curr.sellerId.name.complete}
              >
                <Tooltip
                  id={`avatar-tooltip-${index}`}
                  content={<p>{curr.sellerId.name.complete}</p>}
                  place='top'
                />
                <p
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: 150,
                    paddingLeft: 50
                  }}
                >
                  {curr.sellerId.name.first}{' '}
                  {curr.sellerId.name.last.substring(0, 1)}.
                </p>
                <Avatar
                  data-tip
                  data-for={`avatar-tooltip-${index}`}
                  className={classes.blue}
                  alt={curr.sellerId.name.complete}
                  src={curr.sellerId.photo.url}
                >
                  {!curr.sellerId.photo.url &&
                    `${curr.sellerId.name.first.substring(
                      0,
                      1
                    )}${curr.sellerId.name.last.substring(0, 1)}`}{' '}
                </Avatar>
              </th>
            )
            acc[1].push(
              <TableCell
                key={curr.success + curr.fail + curr.sellerId.name.complete}
                value={curr.success + curr.fail}
                reason='Total de atendimentos'
                defaultCell
              />
            )
            acc[2].push(
              <TableCell
                key={`vendas-${curr.sellerId._id}`}
                value={curr.success}
                textColor='#4B981F'
                reason='Vendas'
                backgroundColor='#F7F7F8'
              />
            )
            acc[3].push(
              <TableCell
                key={`vendas-sistema-${curr.sellerId._id}`}
                value={realSales?.[curr.sellerId._id] ?? 0}
                textColor='#4B981F'
                reason='Vendas Sistema'
                backgroundColor='#F7F7F8'
              />
            )
            acc[4].push(
              <TableCell
                key={`accuracy-${curr.sellerId._id}`}
                value={accuracies?.[curr.sellerId._id] ?? 0}
                isPercentage
                textColor='#FF9800'
                reason='Acurácia'
                backgroundColor='#F7F7F8'
              />
            )
            acc[5].push(
              <TableCell
                key={`conversion-${curr.sellerId._id}`}
                value={(curr.success / (curr.success + curr.fail === 0 ? 1 : curr.success + curr.fail)) * 100}
                isPercentage
                textColor='#4B981F'
                reason='Conversão'
                backgroundColor='#F7F7F8'
              />
            )
            return acc
          },
          [
            [
              <th
                key='Nome Vendedor'
                aria-label='Nome Vendedor'
                style={{
                  maxWidth: '240px',
                  justifyContent: 'flex-end',
                  paddingRight: '8px'
                }}
              />
            ],
            [
              <td
                key='Atendimentos'
                aria-label='Atendimentos'
                style={{
                  maxWidth: '240px',
                  width: '120px',
                  justifyContent: 'flex-end',
                  paddingRight: '8px'
                }}
              >
                <span>Atendimentos</span>
              </td>
            ],
            [
              <td
                key='Vendas'
                aria-label='Vendas'
                style={{
                  maxWidth: '240px',
                  width: '120px',
                  justifyContent: 'flex-end',
                  paddingRight: '8px'
                }}
              >
                <span>Vendas</span>
              </td>
            ],
            [
              <td
                key='Vendas Sistema'
                aria-label='Vendas Sistema'
                style={{
                  maxWidth: '240px',
                  width: '120px',
                  justifyContent: 'flex-end',
                  paddingRight: '8px'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Vendas Sistema
                  <BootstrapTooltip title="Total de vendas do relatório da loja, via integração do sistema">
                    <ErrorOutlineIcon style={{ color: '#C4C4C4', fontSize: 20, marginLeft: 5 }} />
                  </BootstrapTooltip>
                </span>
              </td>
            ],
            [
              <td
                key='Acurácia'
                aria-label='Acurácia'
                style={{
                  maxWidth: '240px',
                  width: '120px',
                  justifyContent: 'flex-end',
                  paddingRight: '8px'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Acurácia
                  <BootstrapTooltip title="Comparação entre vendas efetuadas na lista da vez e vendas do relatório da loja">
                    <ErrorOutlineIcon style={{ color: '#C4C4C4', fontSize: 20, marginLeft: 5 }} />
                  </BootstrapTooltip>
                </span>
              </td>
            ],
            [
              <td
                key='Conversão'
                aria-label='Conversão'
                style={{
                  maxWidth: '240px',
                  width: '120px',
                  justifyContent: 'flex-end',
                  paddingRight: '8px'
                }}
              >
                <span>Conversão</span>
              </td>
            ]
          ]
        )
      )

      setContent(
        sellers
          .reduce<IContent[]>((acc, curr) => {
            curr.events.forEach(event => {
              if (event.selected?.title) {
                const failIndex = acc.findIndex(
                  failEvent => failEvent.name === event.selected.title
                )
                if (failIndex >= 0) {
                  const sellerIndex = acc[failIndex].sellers.findIndex(
                    seller => seller.sellerId === event.sellerId
                  )

                  if (sellerIndex >= 0) {
                    acc[failIndex].sellers[sellerIndex].total += 1
                    acc[failIndex].total += 1
                    acc[failIndex].success = salesCases.includes(
                      acc[failIndex].name
                    )
                  }
                } else {
                  acc.push({
                    name: event.selected.title,
                    sellers: sellers.map(seller => ({
                      sellerId: seller.sellerId._id,
                      total: Number(seller.sellerId._id === event.sellerId)
                    })),
                    total: 1,
                    success: salesCases.includes(event.selected.title)
                  })
                }
              }
            })
            return acc
          }, [])
          // Faz um sort pra deixar as vendas antes das "não-vendas"
          .sort((a, b) => (a.success === b.success ? 0 : a.success ? -1 : 1))
      )
    }
    }, [sellers, accuracies, realSales])

  return (
    <Container>
      <thead>
        {header[0].length > 1 ? (
          header.map((line, index) => (
            <tr key={line[0].key}>
              {index === 0
                ? [
                  ...line,

                  <th
                    style={{ marginBottom: '28px' }}
                    key={`${line[0].key} ${index}`}
                  >
                    <p
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: 150,
                        paddingLeft: 50
                      }}
                    >
                      {store ? store.name : '...'}
                    </p>
                    <Avatar
                      className={classes.blue}
                      alt={store ? store.name : '...'}
                      src={store?.photo?.url ?? undefined}
                    />
                  </th>
                ]
                : index === 1
                  ? [
                    ...line,
                    <TableCell
                      key={`${line[0].key} ${index}`}
                      value={total}
                      reason='Total de atendimentos'
                      defaultCell
                    />
                  ]
                  : index === 2
                    ? [
                      ...line,
                      <TableCell
                        key={`${line[0].key} ${index}`}
                        value={totalSales}
                        textColor='#4B981F'
                        reason='Vendas'
                        backgroundColor='#F7F7F8'
                      />
                    ]
                    : index === 3
                      ? [
                        ...line,
                        <TableCell
                          key={`${line[0].key} ${index}`}
                          value={totalRealSales ?? 0}
                          textColor='#4B981F'
                          reason='Vendas Sistema'
                          backgroundColor='#F7F7F8'
                        />
                      ]
                      : index === 4
                        ? [
                          ...line,
                  <TableCell
                    key={`${line[0].key} ${index}`}
                    value={totalAccuracy ?? 0}
                    isPercentage
                    reason='Acurácia'
                    textColor='#FF9800'
                    backgroundColor='#F7F7F8'
                  />
                ]
              : [
                  ...line,
                  <TableCell
                    key={`${line[0].key} ${index}`}
                    value={(totalSales / total) * 100}
                    isPercentage
                    textColor='#4B981F'
                    reason='Conversão'
                    backgroundColor='#F7F7F8'
                  />
                ]}
            </tr>
          ))
        ) : (
          <MessageContainer>
            <th>
              <EqualizerIcon className={classes.customIcon} />
              <h1>Sem dados registrados</h1>
            </th>
          </MessageContainer>
        )}
      </thead>
      <tbody>
        {content.map(reason => (
          <tr key={reason.name}>
            <td
              style={{
                maxWidth: '240px',
                width: '120px',
                justifyContent: 'flex-end',
                paddingRight: '8px'
              }}
            >
              <span>{reason.name}</span>
            </td>
            {reason.sellers.map(a => (
              <TableCell
                key={a.sellerId}
                value={a.total}
                reason={reason.name}
                backgroundColor={`rgba(${salesCases.includes(reason.name)
                    ? '132, 205, 91'
                    : '73, 91, 212'
                  }, ${Number(
                    (Math.log((a.total * 100) / reason.total) / Math.log(100)) *
                    100
                  ) / 100
                  })`}
              />
            ))}
            <TableCell
              value={reason.total}
              reason={`Total (${reason.name})`}
              defaultCell
            />
          </tr>
        ))}
      </tbody>
    </Container>
  )
}

HeatMap.propTypes = {
  sellers: PropTypes.array,
  totalSales: PropTypes.number
}

export default HeatMap
